import {
	parseUtcTimestamp,
	VehicleDataAge,
	vehicleDataAge,
	vehicleDataAgeInHours,
} from '@agilox/common';
import { UP_TO_DATE_VEHICLE_DATA } from '../constants/up-to-date-vehicle-data.const';
import { VehicleDataActuality, VehicleDataQuery, VehicleTechnicalData } from '../interfaces';
import { VehicleDataType } from '../enums/vehicle-data-type.enum';

const LEVEL_SEVERITY: Record<VehicleDataAge, number> = {
	[VehicleDataAge.UP_TO_DATE]: 0,
	[VehicleDataAge.OUTDATED]: 1,
	[VehicleDataAge.CRITICAL]: 2,
	[VehicleDataAge.MISSING]: 3,
};

const TIMESTAMP_BY_DATA_TYPE: Record<
	VehicleDataType,
	(vehicle: VehicleTechnicalData) => string | null
> = {
	[VehicleDataType.BBOX]: (vehicle: VehicleTechnicalData) => vehicle.latestBbox,
	[VehicleDataType.ORDER]: (vehicle: VehicleTechnicalData) => vehicle.latestOrder,
};

export function toVehicleDataActuality(
	vehicles: VehicleTechnicalData[],
	now: number,
	relevantDataTypes: VehicleDataType[],
	periodEnd?: number
): VehicleDataActuality {
	if (!vehicles.length || !relevantDataTypes.length) {
		return UP_TO_DATE_VEHICLE_DATA;
	}

	const entries = vehicles.map((vehicle: VehicleTechnicalData) =>
		relevantDataTypes.map((dataType: VehicleDataType) => {
			const timestamp = TIMESTAMP_BY_DATA_TYPE[dataType](vehicle);

			return {
				dataType,
				level: coversSelectedPeriod(timestamp, periodEnd)
					? VehicleDataAge.UP_TO_DATE
					: vehicleDataAge(timestamp, now),
				ageInHours: vehicleDataAgeInHours(timestamp, now),
			};
		})
	);

	const worstLevel = entries
		.flat()
		.reduce(
			(worst, entry) => (LEVEL_SEVERITY[entry.level] > LEVEL_SEVERITY[worst] ? entry.level : worst),
			VehicleDataAge.UP_TO_DATE
		);

	const knownAges = entries
		.flat()
		.map(({ ageInHours }) => ageInHours)
		.filter((ageInHours: number | undefined): ageInHours is number => ageInHours !== undefined);
	const oldestAgeInHours = knownAges.length ? Math.floor(Math.max(...knownAges)) : undefined;

	if (worstLevel === VehicleDataAge.UP_TO_DATE) {
		return { ...UP_TO_DATE_VEHICLE_DATA, oldestAgeInHours, totalVehicles: vehicles.length };
	}

	const affectedVehicles = vehicles.filter((_, index: number) =>
		entries[index].some((entry) => entry.level === worstLevel)
	);
	const affectedDataTypes = relevantDataTypes.filter((dataType: VehicleDataType) =>
		entries.flat().some((entry) => entry.dataType === dataType && entry.level === worstLevel)
	);

	return {
		level: worstLevel,
		oldestAgeInHours,
		affectedVehicles,
		affectedDataTypes,
		totalVehicles: vehicles.length,
	};
}

function coversSelectedPeriod(timestamp: string | null, periodEnd: number | undefined): boolean {
	const latest = parseUtcTimestamp(timestamp);

	return !!periodEnd && latest !== undefined && latest > periodEnd;
}

export function toQuerySerials(serials: string[] | undefined): string[] {
	return Array.from(new Set(serials ?? [])).sort();
}

export function sameVehicleDataQuery(
	previous: VehicleDataQuery,
	current: VehicleDataQuery
): boolean {
	return (
		previous.serials.join(',') === current.serials.join(',') &&
		previous.dataTypes.join(',') === current.dataTypes.join(',') &&
		previous.periodEnd === current.periodEnd
	);
}

export function vehicleDataLabel(vehicle: VehicleTechnicalData): string {
	return vehicle.name ? `${vehicle.name} (${vehicle.serial})` : vehicle.serial;
}
